import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './ManageExpenses.css';
import { FaEdit, FaTrash, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import api from './api';
import useAuth from '../hooks/useAuth';
import { canDo } from '../utils/canDo';
import { useSocket } from '../context/SocketContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';

/* ─── helpers ──────────────────────────────────────────────── */
const PIE_COLORS = ['#3b82f6','#ef4444','#10b981','#f59e0b','#8b5cf6','#06b6d4','#f97316','#ec4899','#84cc16','#6366f1'];
const fmt   = n  => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDt = dt => dt ? new Date(dt).toLocaleString('en-IN',{ day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true }) : '';
const fmtDay = dt => dt ? new Date(dt).toLocaleDateString('en-IN',{ weekday:'short', day:'2-digit', month:'short' }) : '';

/* extract readable error from axios/BE response */
function extractError(err) {
  if (err?.response?.data) {
    const d = err.response.data;
    if (typeof d === 'string') return d;
    if (d.message) return d.message;
    if (d.error) return d.error;
    return JSON.stringify(d);
  }
  return err?.message || 'Unknown error';
}

/* Toast component */
function Toast({ toast, onClose }) {
  if (!toast) return null;
  const isErr = toast.type === 'error';
  return (
    <div style={{
      position:'fixed', top:20, right:20, zIndex:99999,
      minWidth:280, maxWidth:420, padding:'14px 20px',
      borderRadius:10, boxShadow:'0 6px 28px rgba(0,0,0,.18)',
      background: isErr ? '#fef2f2' : '#f0fdf4',
      border: `1px solid ${isErr ? '#fca5a5' : '#86efac'}`,
      color: isErr ? '#991b1b' : '#166534',
      fontSize:13, fontWeight:600, lineHeight:1.4,
      display:'flex', alignItems:'flex-start', gap:10,
      animation:'slideInRight .3s ease',
    }}>
      <span style={{fontSize:18}}>{isErr ? '❌' : '✅'}</span>
      <span style={{flex:1}}>{toast.message}</span>
      <button onClick={onClose} style={{background:'none',border:'none',fontSize:16,cursor:'pointer',color:'inherit',lineHeight:1}}>✕</button>
    </div>
  );
}

const defaultExpense = {
  date:'', amount:'', category:'', description:'',
  type:'debit', sender:'', receiver:'', paymentMode:'Cash', transactionId:'Cash', notes:'',
};

function numberToWords(num) {
  if (!num) return '';
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
    'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const nw = (n, sfx='') => { let s=n>19?b[Math.floor(n/10)]+(n%10?' '+a[n%10]:''):a[n]; return s?s+sfx:''; };
  const n = ('000000000'+num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{3})$/);
  if(!n) return '';
  return (nw(+n[1],' Crore ')+nw(+n[2],' Lakh ')+nw(+n[3],' Thousand ')+nw(+n[4].slice(0,1),' Hundred ')+nw(+n[4].slice(1))+' Rupees').trim();
}

/* ─── Fuzzy description normalisation ───────────────────────── */
function normStr(s) { return (s||'').toLowerCase().trim().replace(/\s+/g,' '); }
function levenshtein(a,b) {
  if(a===b) return 0;
  const m=a.length,n=b.length;
  if(!m) return n; if(!n) return m;
  let prev=Array.from({length:n+1},(_,i)=>i), curr=Array(n+1).fill(0);
  for(let i=1;i<=m;i++){
    curr[0]=i;
    for(let j=1;j<=n;j++)
      curr[j]=a[i-1]===b[j-1]?prev[j-1]:1+Math.min(prev[j],curr[j-1],prev[j-1]);
    const tmp=prev; prev=curr; curr=tmp;
  }
  return prev[n];
}
// Returns an existing canonical description if within typo-threshold, else normalised input
function fuzzyCanonical(str, canonicals) {
  const norm=normStr(str);
  if(!norm||!canonicals.length) return norm;
  let best=null,bestDist=Infinity;
  canonicals.forEach(c=>{ const d=levenshtein(norm,normStr(c)); if(d<bestDist){bestDist=d;best=c;} });
  return (best && bestDist<=Math.max(1,Math.floor(norm.length/5))) ? best : norm;
}
// Groups expenses by fuzzy-normalised description → [{name, debit, credit}]
function groupByFuzzyDesc(expenses) {
  const groups=[];
  expenses.forEach(e=>{
    const norm=normStr(e.description);
    if(!norm) return;
    const thr=Math.max(1,Math.floor(norm.length/5));
    let g=groups.find(g=>levenshtein(g.key,norm)<=thr);
    if(!g){g={key:norm,debit:0,credit:0};groups.push(g);}
    const amt=parseFloat(e.amount)||0;
    if(e.type==='credit') g.credit+=amt; else g.debit+=amt;
  });
  return groups
    .map(g=>({name:g.key,debit:Math.round(g.debit),credit:Math.round(g.credit)}))
    .sort((a,b)=>(b.debit+b.credit)-(a.debit+a.credit));
}
// Builds normStr(raw) → canonical (most-frequent raw variant wins)
function buildCanonicalMap(expenses) {
  const groups=[];
  expenses.forEach(e=>{
    const raw=(e.description||'').trim();
    if(!raw) return;
    const norm=normStr(raw);
    const thr=Math.max(1,Math.floor(norm.length/5));
    let g=groups.find(g=>levenshtein(g.key,norm)<=thr);
    if(!g){g={key:norm,forms:{}};groups.push(g);}
    g.forms[raw]=(g.forms[raw]||0)+1;
  });
  const map={};
  groups.forEach(g=>{
    const canonical=Object.entries(g.forms).sort((a,b)=>b[1]-a[1])[0][0];
    Object.keys(g.forms).forEach(raw=>{map[normStr(raw)]=canonical;});
  });
  return map;
}

/* ─── Description Autocomplete ──────────────────────────────── */
function DescriptionAutocomplete({ value, onChange, suggestions, name, required, style }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function onOut(ev) { if(wrapRef.current && !wrapRef.current.contains(ev.target)) setOpen(false); }
    document.addEventListener('mousedown', onOut);
    return () => document.removeEventListener('mousedown', onOut);
  }, []);

  const q = normStr(value);
  const matches = useMemo(() => {
    if(!q) return suggestions.slice(0, 8);
    const prefix = suggestions.filter(s => normStr(s).startsWith(q));
    const fuzzy  = suggestions.filter(s => !normStr(s).startsWith(q) && levenshtein(normStr(s).slice(0, q.length), q) <= 1);
    return [...prefix, ...fuzzy].filter((s,i,arr)=>arr.indexOf(s)===i).slice(0, 8);
  }, [q, suggestions]);

  return (
    <div ref={wrapRef} style={{ position:'relative', ...style }}>
      <input
        type="text" name={name} value={value} required={required}
        onChange={e => { onChange(e); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Type description…"
        style={{ width:'100%', boxSizing:'border-box' }}
      />
      {open && matches.length > 0 && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0, zIndex:9999,
          background:'#fff', border:'1px solid #d1d5db', borderRadius:6,
          boxShadow:'0 4px 16px rgba(0,0,0,.14)', maxHeight:200, overflowY:'auto',
        }}>
          {matches.map(s=>(
            <div key={s}
              onMouseDown={ev=>{ ev.preventDefault(); onChange({target:{name,value:s}}); setOpen(false); }}
              style={{ padding:'7px 12px', fontSize:12, cursor:'pointer', borderBottom:'1px solid #f3f4f6' }}
              onMouseEnter={ev=>ev.currentTarget.style.background='#f0f9ff'}
              onMouseLeave={ev=>ev.currentTarget.style.background=''}
            >{s}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Edit Expense Modal ─────────────────────────────────────── */
function EditExpenseModal({ expense, id, categories:initCats, senders:initSnds, receivers:initRcvs, descriptions, allExpenses, onSave, onClose, showToast }) {
  const [form, setForm] = useState({
    amount:        expense.amount        || '',
    type:          expense.type          || 'debit',
    sender:        expense.sender        || '',
    receiver:      expense.receiver      || '',
    category:      expense.category      || '',
    description:   expense.description   || '',
    paymentMode:   expense.paymentMode   || 'Cash',
    transactionId: expense.transactionId || 'Cash',
    notes:         expense.notes         || '',
  });
  const [amtWords, setAmtWords] = useState(numberToWords(parseInt(expense.amount,10)));
  const [cats, setCats] = useState(initCats);
  const [snds, setSnds] = useState(initSnds);
  const [rcvs, setRcvs] = useState(initRcvs);

  function handleChange(e) {
    const { name, value } = e.target;
    if(name==='description'){
      if(/^(p[ea]t+t*y\s*cash|cash)$/i.test(value.trim())){alert("Petty/Cash not allowed.");return;}
      setForm(p=>({...p,description:value})); return;
    }
    if(name==='amount'){ setForm(p=>({...p,amount:value})); setAmtWords(numberToWords(parseInt(value,10))); return; }
    if(name==='Mode'){ setForm(p=>({...p,paymentMode:value,transactionId:value==='Cash'?'Cash':''})); return; }
    if(name==='transactionId'&&form.paymentMode==='Cash') return;
    if(name==='sender'){ setForm(p=>({...p,sender:value})); if(value&&!cats.includes(value)) setCats(p=>[...p,value]); return; }
    if(name==='receiver'){ setForm(p=>({...p,receiver:value})); return; }
    if(name==='category'){
      setForm(p=>({...p,category:value}));
      if(!form.sender){setForm(p=>({...p,sender:value})); if(value&&!cats.includes(value)) setCats(p=>[...p,value]);}
      return;
    }
    setForm(p=>({...p,[name]:value}));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if(!window.confirm(`क्या ₹${form.amount} की expense सही है ?`)) return;
    try {
      const allDescs=[...descriptions,...allExpenses.map(ex=>normStr(ex.description)).filter(Boolean)];
      const canonDesc=fuzzyCanonical(form.description, allDescs);
      await api.put(`${process.env.REACT_APP_URL}/expenses/${id}`, {...form, description:canonDesc});
      if (showToast) showToast('success', `Expense ₹${form.amount} updated successfully`);
      onSave();
    } catch(err){ console.error(err); if (showToast) showToast('error', 'Update failed: ' + extractError(err)); }
  }

  const F = { marginBottom:12 };
  const L = { display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 };
  const I = { width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' };

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:10000,
      background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center',
    }} onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={{
        background:'#fff', borderRadius:12, padding:'22px 26px',
        width:'90%', maxWidth:540, maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 8px 40px rgba(0,0,0,.25)',
      }}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
          <div style={{fontSize:16,fontWeight:700,color:'#1f2937'}}>✏️ Edit Expense</div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#6b7280',lineHeight:1}}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={F}>
            <label style={L}>Amount</label>
            <input type="number" name="amount" value={form.amount} onChange={handleChange} required style={I}/>
            {amtWords&&<small style={{color:'red',fontSize:13,fontStyle:'italic',fontWeight:700}}>{amtWords}</small>}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div style={F}>
              <label style={L}>Type</label>
              <select name="type" value={form.type} onChange={handleChange} required style={I}>
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>
            </div>
            <div style={F}>
              <label style={L}>Payment Mode</label>
              <select name="Mode" value={form.paymentMode} onChange={handleChange} required style={I}>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Paytm">Paytm</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div style={F}>
              <label style={L}>Sender</label>
              <select name="sender" value={form.sender} onChange={handleChange} required style={{...I,marginBottom:4}}>
                <option value="">Select Sender</option>
                {snds.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
              <input type="text" placeholder="Or type new sender" value={form.sender} onChange={handleChange} name="sender"
                onBlur={()=>{ if(form.sender&&!snds.includes(form.sender)) setSnds(p=>[...p,form.sender]); }} style={I}/>
            </div>
            <div style={F}>
              <label style={L}>Receiver</label>
              <select name="receiver" value={form.receiver} onChange={handleChange} required style={{...I,marginBottom:4}}>
                <option value="">Select Receiver</option>
                {rcvs.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
              <input type="text" placeholder="Or type new receiver" value={form.receiver} onChange={handleChange} name="receiver"
                onBlur={()=>{ if(form.receiver&&!rcvs.includes(form.receiver)) setRcvs(p=>[...p,form.receiver]); }} style={I}/>
            </div>
          </div>
          <div style={F}>
            <label style={L}>Category</label>
            <select name="category" value={form.category} onChange={handleChange} required style={{...I,marginBottom:4}}>
              <option value="">Select Category</option>
              {cats.map(o=><option key={o} value={o}>{o}</option>)}
            </select>
            <input type="text" placeholder="Or type new category" value={form.category} onChange={handleChange} name="category"
              onBlur={()=>{ const v=form.category.trim(); if(v&&!cats.includes(v)) setCats(p=>[...p,v]); }} style={I}/>
          </div>
          <div style={F}>
            <label style={L}>Description</label>
            <DescriptionAutocomplete
              name="description" value={form.description} onChange={handleChange}
              suggestions={descriptions} required style={{}}
            />
          </div>
          <div style={F}>
            <label style={L}>Transaction ID</label>
            <input type="text" name="transactionId" value={form.transactionId} onChange={handleChange} required
              disabled={form.paymentMode==='Cash'} placeholder={form.paymentMode==='Cash'?'Cash':'Enter Transaction ID'} style={I}/>
          </div>
          <div style={F}>
            <label style={L}>Notes</label>
            <textarea name="notes" value={form.notes||''} onChange={handleChange} placeholder="Any additional notes..."
              style={{...I,minHeight:40,resize:'vertical',fontFamily:'inherit'}}/>
          </div>
          <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
            <button type="button" onClick={onClose}
              style={{padding:'8px 20px',borderRadius:6,border:'1px solid #d1d5db',background:'#f9fafb',cursor:'pointer',fontSize:13}}>
              Cancel
            </button>
            <button type="submit"
              style={{padding:'8px 24px',borderRadius:6,border:'none',background:'#2563eb',color:'#fff',fontWeight:700,cursor:'pointer',fontSize:13}}>
              Update Expense
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Analytics panel ───────────────────────────────────────── */
function ExpenseAnalytics({ monthExpenses, allExpenses }) {
  const [showDetailed, setShowDetailed] = useState(false);
  const debits  = useMemo(() => monthExpenses.filter(e => e.type !== 'credit'), [monthExpenses]);
  const credits = useMemo(() => monthExpenses.filter(e => e.type === 'credit'), [monthExpenses]);
  const totalDebit  = debits.reduce((s,e)  => s + (parseFloat(e.amount)||0), 0);
  const totalCredit = credits.reduce((s,e) => s + (parseFloat(e.amount)||0), 0);
  const net = totalCredit - totalDebit;

  const byCat = useMemo(() => {
    const m = {};
    debits.forEach(e => { const k = e.category||'Uncategorised'; m[k]=(m[k]||0)+(parseFloat(e.amount)||0); });
    return Object.entries(m).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);
  }, [debits]);

  const byReceiver = useMemo(() => {
    const m = {};
    debits.forEach(e => { const k = e.receiver||'Unknown'; m[k]=(m[k]||0)+(parseFloat(e.amount)||0); });
    return Object.entries(m).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value).slice(0,8);
  }, [debits]);

  const byDesc = useMemo(() => groupByFuzzyDesc(monthExpenses), [monthExpenses]);

  const byMonth = useMemo(() => {
    const m = {};
    allExpenses.forEach(e => {
      const d = new Date(e.date); if(isNaN(d)) return;
      const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      if(!m[k]) m[k]={month:k,debit:0,credit:0};
      if(e.type==='credit') m[k].credit+=parseFloat(e.amount)||0;
      else                  m[k].debit +=parseFloat(e.amount)||0;
    });
    return Object.values(m).sort((a,b)=>a.month.localeCompare(b.month)).slice(-12);
  }, [allExpenses]);

  const overallCredit = allExpenses.reduce((s,e)=>s+(e.type==='credit'?parseFloat(e.amount)||0:0),0);
  const overallDebit  = allExpenses.reduce((s,e)=>s+(e.type!=='credit'?parseFloat(e.amount)||0:0),0);
  const overallBalance = overallCredit - overallDebit;

  const C = { background:'#fff', borderRadius:10, padding:'14px 18px', border:'1px solid #e5e7eb', boxShadow:'0 1px 6px rgba(0,0,0,.06)' };
  const T = { fontSize:13, fontWeight:700, color:'#374151', marginBottom:10 };

  return (
    <div style={{ marginBottom:24 }}>
      {/* ── Balance Chips (always visible when analytics open) ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:16}}>
        {[
          { label:'Range Credit', val:fmt(totalCredit), sub:`${credits.length} txn`, color:'#10b981', bg:'linear-gradient(135deg,#ecfdf5,#d1fae5)' },
          { label:'Range Debit',  val:fmt(totalDebit),  sub:`${debits.length} txn`, color:'#ef4444', bg:'linear-gradient(135deg,#fef2f2,#fee2e2)' },
          { label:'Range Net',    val:(net>=0?'+':'')+fmt(net), sub:'credit − debit', color:net>=0?'#10b981':'#ef4444', bg:'linear-gradient(135deg,#eff6ff,#dbeafe)' },
          { label:'Overall Balance', val:(overallBalance>=0?'+':'')+fmt(overallBalance), sub:`All time (${allExpenses.length} txn)`, color:overallBalance>=0?'#059669':'#dc2626', bg:'linear-gradient(135deg,#f0fdf4,#bbf7d0)' },
          { label:'Overall Credit',  val:fmt(overallCredit), sub:'All time', color:'#059669', bg:'linear-gradient(135deg,#ecfdf5,#a7f3d0)' },
          { label:'Overall Debit',   val:fmt(overallDebit),  sub:'All time', color:'#b91c1c', bg:'linear-gradient(135deg,#fef2f2,#fecaca)' },
          { label:'Top Category', val:byCat[0]?.name||'—', sub:byCat[0]?fmt(byCat[0].value):'', color:'#f59e0b', bg:'linear-gradient(135deg,#fffbeb,#fef3c7)' },
        ].map(c=>(
          <div key={c.label} style={{ background:c.bg, borderRadius:10, padding:'12px 14px', border:'1px solid rgba(0,0,0,.06)' }}>
            <div style={{fontSize:10,color:'#6b7280',fontWeight:700,textTransform:'uppercase',letterSpacing:.5}}>{c.label}</div>
            <div style={{fontSize:18,fontWeight:800,color:c.color,marginTop:3}}>{c.val}</div>
            <div style={{fontSize:10,color:'#9ca3af',marginTop:1}}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Monthly Trend (always visible when analytics open) ── */}
      <div style={{...C, marginBottom:14}}>
        <div style={T}>Monthly Trend</div>
        <ResponsiveContainer width="100%" height={170}>
          <LineChart data={byMonth} margin={{left:0,right:20}}>
            <CartesianGrid strokeDasharray="3 3"/>
            <XAxis dataKey="month" tick={{fontSize:10}}/>
            <YAxis tick={{fontSize:10}} tickFormatter={v=>'₹'+(v/1000).toFixed(0)+'k'}/>
            <Tooltip formatter={v=>fmt(v)}/>
            <Legend/>
            <Line type="monotone" dataKey="debit"  stroke="#ef4444" strokeWidth={2} dot={false} name="Debit"/>
            <Line type="monotone" dataKey="credit" stroke="#10b981" strokeWidth={2} dot={false} name="Credit"/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── Toggle for detailed charts ── */}
      <div style={{textAlign:'center',marginBottom:14}}>
        <button onClick={()=>setShowDetailed(v=>!v)}
          style={{padding:'8px 20px',borderRadius:8,border:'1.5px solid #d1d5db',background:showDetailed?'#4f46e5':'#fff',
            color:showDetailed?'#fff':'#374151',fontSize:12,fontWeight:700,cursor:'pointer',transition:'all 0.15s'}}>
          {showDetailed ? 'Hide Detailed Charts' : 'Show Detailed Charts'}
        </button>
      </div>

      {/* ── Detailed charts (toggled) ── */}
      {showDetailed && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div style={C}>
              <div style={T}>Spend by Description (Range) <span style={{fontSize:10,color:'#9ca3af',fontWeight:400}}>(fuzzy-grouped)</span></div>
              <ResponsiveContainer width="100%" height={Math.max(200, byDesc.length * 28)}>
                <BarChart data={byDesc} layout="vertical" margin={{left:10,right:20}}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                  <XAxis type="number" tick={{fontSize:10}} tickFormatter={v=>'₹'+(v/1000).toFixed(0)+'k'}/>
                  <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={110}/>
                  <Tooltip formatter={v=>fmt(v)}/>
                  <Legend wrapperStyle={{fontSize:10}}/>
                  <Bar dataKey="debit"  stackId="a" fill="#ef4444" name="Debit"  radius={[0,0,0,0]}/>
                  <Bar dataKey="credit" stackId="a" fill="#10b981" name="Credit" radius={[0,4,4,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={C}>
              <div style={T}>Top Receivers — Range (Debit)</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={byReceiver} layout="vertical" margin={{left:10,right:20}}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                  <XAxis type="number" tick={{fontSize:10}} tickFormatter={v=>'₹'+(v/1000).toFixed(0)+'k'}/>
                  <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={90}/>
                  <Tooltip formatter={v=>fmt(v)}/>
                  <Bar dataKey="value" fill="#3b82f6" radius={[0,4,4,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={C}>
            <div style={T}>Cost Cutting — Top 5 Categories (Range)</div>
            {byCat.slice(0,5).map((c,i)=>{
              const pct = totalDebit>0?(c.value/totalDebit)*100:0;
              return (
                <div key={c.name} style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
                  <span style={{fontSize:12,fontWeight:700,color:'#6b7280',minWidth:20}}>#{i+1}</span>
                  <span style={{fontSize:12,fontWeight:600,minWidth:140,color:'#1f2937'}}>{c.name}</span>
                  <div style={{flex:1,background:'#f3f4f6',borderRadius:99,height:8,overflow:'hidden'}}>
                    <div style={{width:`${pct}%`,background:PIE_COLORS[i],height:'100%',borderRadius:99}}/>
                  </div>
                  <span style={{fontSize:12,fontWeight:700,color:PIE_COLORS[i],minWidth:70,textAlign:'right'}}>{fmt(c.value)}</span>
                  <span style={{fontSize:11,color:'#9ca3af',minWidth:36}}>{pct.toFixed(1)}%</span>
                </div>
              );
            })}
            <div style={{marginTop:12,padding:'10px 14px',background:'#fef9c3',borderRadius:8,border:'1px solid #fde047',fontSize:12,color:'#854d0e'}}>
              <strong>Suggestions to reduce expenses:</strong>
              <ul style={{margin:'6px 0 0 16px',lineHeight:1.7}}>
                <li>Review recurring payments monthly — cancel unused subscriptions</li>
                <li>Set a per-category monthly budget cap and get alerts when exceeded</li>
                <li>Consolidate vendor payments to negotiate bulk discounts</li>
                <li>Require dual approval for transactions above ₹5,000</li>
                <li>Use digital payments (UPI/bank transfer) — avoids petty cash leakage</li>
                <li>Compare top 3 receiver spends quarterly — reduce or renegotiate</li>
                <li>Track OTT/unverified entries — missing records hide hidden costs</li>
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Daily OTT tracker ─────────────────────────────────────── */
function DailyTracker({ expenses, selectedMonth, onMarkOTT, showAudit }) {
  const [year, month] = selectedMonth.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();

  const byDay = useMemo(() => {
    const m = {};
    expenses.forEach(e => {
      const d = new Date(e.date);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!m[key]) m[key] = [];
      m[key].push(e);
    });
    return m;
  }, [expenses]);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const currentMonthStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}`;

  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'14px 18px', marginBottom:16, boxShadow:'0 1px 6px rgba(0,0,0,.06)' }}>
      <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:10 }}>
        📅 Daily Entry Tracker — {selectedMonth}
        <span style={{ fontSize:11, fontWeight:400, color:'#9ca3af', marginLeft:8 }}>
          🟢 entries  🟠 OTT only  🔴 missing{showAudit ? '  ✎ edited' : ''}
        </span>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
        {Array.from({length:daysInMonth},(_,i)=>{
          const dayNum = i+1;
          const key = `${year}-${String(month).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`;
          const dayEntries = byDay[key] || [];
          const isOTTOnly  = dayEntries.length>0 && dayEntries.every(e=>e.category==='OTT');
          const hasReal    = dayEntries.some(e=>e.category!=='OTT');
          const hasEdits   = showAudit && dayEntries.some(e=>e.editLog?.length>0);
          const editCount  = showAudit ? dayEntries.reduce((s,e)=>s+(e.editLog?.length||0),0) : 0;
          const isFuture   = selectedMonth===currentMonthStr && key>todayStr;
          const isMissing  = !isFuture && dayEntries.length===0;
          const isToday    = key===todayStr;

          let bg='#f0fdf4', border='#86efac', color='#16a34a', title=`${dayEntries.length} entries`;
          if (isFuture)     { bg='#f8fafc'; border='#e2e8f0'; color='#94a3b8'; title='Future'; }
          else if(isMissing){ bg='#fef2f2'; border='#fca5a5'; color='#dc2626'; title='No entries — click to mark OTT'; }
          else if(isOTTOnly){ bg='#fff7ed'; border='#fed7aa'; color='#c2410c'; title='OTT only'; }
          if(hasEdits && !isFuture && !isMissing) {
            border='#f59e0b'; title+=` ⚠ ${editCount} edit(s) — review for tampering`;
          }

          return (
            <div key={key}
              onClick={() => isMissing && onMarkOTT(key)}
              title={title}
              style={{
                width:42, height:42, borderRadius:8, display:'flex', flexDirection:'column',
                alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700,
                background:bg, border:`1.5px solid ${border}`, color,
                cursor:isMissing?'pointer':'default',
                outline: isToday?`2px solid #3b82f6`:'none', outlineOffset:1,
                position:'relative',
              }}>
              <span>{dayNum}</span>
              {hasReal    && <span style={{fontSize:7,marginTop:1}}>✓{dayEntries.filter(e=>e.category!=='OTT').length}</span>}
              {isOTTOnly  && <span style={{fontSize:7,marginTop:1}}>OTT</span>}
              {isMissing  && <span style={{fontSize:7,marginTop:1}}>+OTT</span>}
              {hasEdits && (
                <span style={{
                  position:'absolute', top:-5, right:-5, background:'#f59e0b', color:'#fff',
                  borderRadius:'50%', width:14, height:14, fontSize:8, fontWeight:800,
                  display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1,
                }}>✎</span>
              )}
            </div>
          );
        })}
      </div>
      <div style={{marginTop:8, fontSize:11, color:'#6b7280'}}>
        🔴 Click any red day to auto-create an OTT entry for Veena to verify.
      </div>
    </div>
  );
}

/* ─── Expanded row detail ────────────────────────────────────── */
function ExpandedRow({ e, colSpan, showAudit }) {
  const fields = [
    ['Date',           fmtDt(e.date)],
    ['Category',       e.category],
    ['Sender',         e.sender],
    ['Receiver',       e.receiver],
    ['Description',    e.description],
    ['Payment Mode',   e.paymentMode],
    ['Transaction ID', e.transactionId],
    ['Balance After',  fmt(e.balanceAfterTxn)],
    ['Type',           e.type],
    ...(e.notes ? [['Notes', e.notes]] : []),
  ];
  return (
    <tr>
      <td colSpan={colSpan} style={{ background:'#f8fafc', padding:'12px 20px', borderBottom:'2px solid #e5e7eb' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'8px 24px' }}>
          {fields.map(([label,val]) => (
            <div key={label} style={{ fontSize:12 }}>
              <span style={{ color:'#6b7280', fontWeight:600 }}>{label}: </span>
              <span style={{ color:e.category==='OTT'&&label==='Category'?'#c2410c':'#1f2937',
                fontWeight:e.category==='OTT'&&label==='Category'?700:400 }}>{val||'—'}</span>
            </div>
          ))}
        </div>
        {e.category==='OTT' && (
          <div style={{ marginTop:8, padding:'6px 10px', background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:6, fontSize:12, color:'#92400e' }}>
            ⚠️ <strong>OTT Entry</strong> — This day has no verified expense records. Veena please update with actual transactions.
          </div>
        )}
        {showAudit && e.editLog?.length > 0 && (
          <div style={{ marginTop:10, padding:'8px 12px', background:'#fffbeb', border:'1px solid #fbbf24', borderRadius:6 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#92400e', marginBottom:6 }}>
              ✎ Edit History ({e.editLog.length} change{e.editLog.length>1?'s':''}) — review for accuracy
            </div>
            {[...e.editLog].reverse().map((log, i) => (
              <div key={i} style={{ fontSize:11, color:'#78350f', marginBottom:4, paddingBottom:4,
                borderBottom: i < e.editLog.length-1 ? '1px solid #fde68a' : 'none' }}>
                <span style={{ fontWeight:700 }}>{log.editedBy}</span>
                {' on '}{log.editedAt ? new Date(log.editedAt).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true}) : '—'}
                {' — was: '}
                <span style={{ fontFamily:'monospace', background:'#fef3c7', padding:'0 4px', borderRadius:3 }}>
                  {log.before?.type==='credit'?'↑':'↓'}₹{log.before?.amount}
                  {log.before?.description ? ` "${log.before.description}"` : ''}
                  {log.before?.category ? ` [${log.before.category}]` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

/* ─── Main component ────────────────────────────────────────── */
export default function ManageExpenses({ monthlyBudget = 10000 }) {
  const today     = new Date();

  // Date range: default 1st of current month → today
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const toISO = d => d.toISOString().slice(0,10);
  const [dateFrom, setDateFrom] = useState(toISO(monthStart));
  const [dateTo,   setDateTo]   = useState(toISO(today));

  const [expenses,       setExpenses]       = useState([]);
  const [allExpenses,    setAllExpenses]     = useState([]);
  const [newExpense,     setNewExpense]      = useState(defaultExpense);
  const [categories,     setCategories]     = useState([]);
  const [senders,        setSenders]        = useState([]);
  const [receivers,      setReceivers]      = useState([]);
  const [descriptions,   setDescriptions]   = useState([]);
  const [sortKey,        setSortKey]        = useState('date');
  const [sortAsc,        setSortAsc]        = useState(false);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDesc,     setFilterDesc]     = useState('');
  const [filterType,     setFilterType]     = useState(''); // '' | 'credit' | 'debit'
  const [showForm,       setShowForm]       = useState(false);
  const [amountInWords,  setAmountInWords]  = useState('');
  const [showAnalytics,  setShowAnalytics]  = useState(false);
  const [showTracker,    setShowTracker]    = useState(true);
  const [expandedRows,   setExpandedRows]   = useState(new Set());
  const [editAlerts,     setEditAlerts]     = useState([]);
  const [editPopup,      setEditPopup]      = useState(null); // { id, expense }
  const [selectedExpIds, setSelectedExpIds] = useState(new Set());
  const [toast, setToast] = useState(null); // { type:'success'|'error', message }
  const [deleting, setDeleting] = useState(false);
  const [showCashMatch, setShowCashMatch] = useState(false);
  const toastTimer = useRef(null);
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);
  const { auth } = useAuth();
  const { socketRef } = useSocket() || {};

  const isFullAccess  = canDo(auth, 'expenses.approve');
  const canAdd  = canDo(auth, 'expenses.add');
  const canEdit = canDo(auth, 'expenses.edit');
  const canEditDelete = canEdit;
  const isSuperAdmin    = canEdit;
  const isAnalyticsRole = (auth?.roles || []).some(r => ['owner', 'solution', 'supervisor'].includes(r));
  const isNotifyRole    = isFullAccess;

  /* fetch range expenses */
  const fetchRangeExpenses = useCallback(() => {
    const from = new Date(dateFrom + 'T00:00:00').toISOString();
    const to   = new Date(dateTo   + 'T23:59:59').toISOString();
    api.get(`${process.env.REACT_APP_URL}/expenses?limit=9999&page=1&from=${from}&to=${to}`)
      .then(res => {
        const raw = res.data?.expenses || [];
        const canonMap = buildCanonicalMap(raw);
        const data = raw.map(e => ({
          ...e,
          description: e.description ? (canonMap[normStr(e.description)] || e.description) : e.description,
        }));
        setExpenses(data);
      })
      .catch(console.error);
  }, [dateFrom, dateTo]);

  /* socket: listen for expense edits */
  useEffect(() => {
    const sock = socketRef?.current;
    if (!sock || !isNotifyRole) return;
    const handler = (data) => {
      setEditAlerts(prev => [data, ...prev].slice(0, 10));
      fetchRangeExpenses();
      fetchAllExpenses();
    };
    sock.on('expense:edited', handler);
    return () => sock.off('expense:edited', handler);
  }, [socketRef, isNotifyRole, fetchRangeExpenses]);

  /* fetch ALL expenses (for dropdowns, analytics, overall balance) */
  const fetchAllExpenses = useCallback(() => {
    api.get(`${process.env.REACT_APP_URL}/expenses?limit=9999&page=1`)
      .then(res => {
        const raw = res.data?.expenses || [];
        setAllExpenses(raw);
        // Build dropdown values from ALL expenses (not filtered by date)
        const cats=[], snds=[], rcvs=[], descs=[];
        raw.forEach(e => {
          if(e.category    && !cats.includes(e.category))    cats.push(e.category);
          if(e.sender      && !snds.includes(e.sender))      snds.push(e.sender);
          if(e.receiver    && !rcvs.includes(e.receiver))    rcvs.push(e.receiver);
          if(e.description && !descs.includes(e.description)) descs.push(e.description);
        });
        setCategories(cats); setSenders(snds); setReceivers(rcvs); setDescriptions(descs);
      })
      .catch(console.error);
  }, []);

  useEffect(() => { fetchRangeExpenses(); }, [fetchRangeExpenses]);
  useEffect(() => { fetchAllExpenses(); }, [fetchAllExpenses]);

  /* mark OTT for a day */
  const handleMarkOTT = useCallback(async (dateStr) => {
    if (!window.confirm(`Mark ${dateStr} as OTT entry for Veena to verify?`)) return;
    try {
      await api.post(`${process.env.REACT_APP_URL}/expenses`, {
        date:          new Date(dateStr + 'T23:59:00').toISOString(),
        amount:        0,
        type:          'debit',
        category:      'OTT',
        description:   'OTT – Daily entry not filed. Veena to verify.',
        sender:        'System',
        receiver:      'Veena',
        paymentMode:   'Cash',
        transactionId: 'OTT',
      });
      fetchRangeExpenses();
    } catch(e) { alert('Failed to create OTT entry'); }
  }, [fetchRangeExpenses]);

  function handleChange(e) {
    const { name, value } = e.target;
    if(name==='description') {
      if(/^(p[ea]t+t*y\s*cash|cash)$/i.test(value.trim())){window.alert("Petty/Cash not allowed.");return;}
      setNewExpense(p=>({...p,description:value})); return;
    }
    if(name==='amount'){ setNewExpense(p=>({...p,amount:value})); setAmountInWords(numberToWords(parseInt(value,10))); return; }
    if(name==='Mode'){
      setNewExpense(p=>({...p,paymentMode:value,transactionId:value==='Cash'?'Cash':''})); return;
    }
    if(name==='sender'){ setNewExpense(p=>({...p,sender:value})); if(value&&!categories.includes(value)) setCategories(p=>[...p,value]); return; }
    if(name==='receiver'){ setNewExpense(p=>({...p,receiver:value})); return; }
    if(name==='category'){
      setNewExpense(p=>({...p,category:value}));
      if(!newExpense.sender){setNewExpense(p=>({...p,sender:value})); if(value&&!categories.includes(value)) setCategories(p=>[...p,value]);}
      return;
    }
    if(name==='transactionId'&&newExpense.paymentMode==='Cash') return;
    setNewExpense(p=>({...p,[name]:value}));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(!window.confirm(`क्या ₹${newExpense.amount} की expense सही है ?`)) return;
    try {
      const allDescs = [...descriptions, ...allExpenses.map(e=>normStr(e.description)).filter(Boolean)];
      const canonDesc = fuzzyCanonical(newExpense.description, allDescs);
      const payload = { ...newExpense, description: canonDesc };
      var expDate = newExpense.date ? new Date(newExpense.date+'T12:00:00').toISOString() : new Date().toISOString();
      await api.post(`${process.env.REACT_APP_URL}/expenses`,{...payload,date:expDate,amount:parseFloat(newExpense.amount)||0});
      setNewExpense(defaultExpense); setShowForm(false); setAmountInWords('');
      showToast('success', `Expense ₹${newExpense.amount} added successfully`);
      fetchRangeExpenses(); fetchAllExpenses();
    } catch(err){ console.error(err); showToast('error', 'Failed to save: ' + extractError(err)); }
  };

  const handleEdit = (id, exp) => setEditPopup({ id, expense: exp });

  const handleBulkDelete = async () => {
    if (selectedExpIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedExpIds.size} expense(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await api.post(`${process.env.REACT_APP_URL}/expenses/bulk-delete`, { ids: Array.from(selectedExpIds) });
      setSelectedExpIds(new Set());
      showToast('success', typeof res.data === 'string' ? res.data : `${selectedExpIds.size} expense(s) deleted successfully`);
      fetchRangeExpenses(); fetchAllExpenses();
    } catch (err) { console.error(err); showToast('error', 'Bulk delete failed: ' + extractError(err)); }
    setDeleting(false);
  };

  const toggleExpSelect = (id) => {
    setSelectedExpIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleSelectAll = () => {
    if (selectedExpIds.size === sorted.length) setSelectedExpIds(new Set());
    else setSelectedExpIds(new Set(sorted.map(e => e._id)));
  };

  const handleDelete = async (id, exp) => {
    if(!window.confirm(`Delete? ${exp.description} — ₹${exp.amount}`)) return;
    setDeleting(true);
    try {
      await api.delete(`${process.env.REACT_APP_URL}/expenses/${id}`);
      showToast('success', `Deleted: ${exp.description} — ₹${exp.amount}`);
      fetchRangeExpenses(); fetchAllExpenses();
    } catch(err) {
      console.error(err);
      showToast('error', 'Delete failed: ' + extractError(err));
    }
    setDeleting(false);
  };

  const toggleRow = id => setExpandedRows(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });

  const filtered = expenses.filter(e => {
    if (filterType === 'credit' && e.type !== 'credit') return false;
    if (filterType === 'debit' && e.type === 'credit') return false;
    if (filterCategory && e.category !== filterCategory) return false;
    if (filterDesc && !(e.description || '').toLowerCase().includes(filterDesc.toLowerCase())) return false;
    if(!searchQuery) return true;
    return Object.values(e).some(v=>String(v||'').toLowerCase().includes(searchQuery.toLowerCase()));
  });

  // Unique categories for filter dropdown
  const uniqueCategories = useMemo(() => [...new Set(expenses.map(e => e.category).filter(Boolean))].sort(), [expenses]);

  const sorted = useMemo(()=>[...filtered].sort((a,b)=>{
    let va=a[sortKey], vb=b[sortKey];
    if(sortKey==='date'||sortKey==='amount'){ va=sortKey==='amount'?parseFloat(va):new Date(va); vb=sortKey==='amount'?parseFloat(vb):new Date(vb); }
    if(va<vb) return sortAsc?-1:1; if(va>vb) return sortAsc?1:-1; return 0;
  }), [filtered, sortKey, sortAsc]);

  const totalCredit = expenses.reduce((s,e)=>s+(e.type==='credit'?parseFloat(e.amount)||0:0),0);
  const totalDebit  = expenses.reduce((s,e)=>s+(e.type!=='credit'?parseFloat(e.amount)||0:0),0);
  const ottCount    = expenses.filter(e=>e.category==='OTT').length;

  // Overall balance (from ALL expenses ever)
  const overallCredit = allExpenses.reduce((s,e)=>s+(e.type==='credit'?parseFloat(e.amount)||0:0),0);
  const overallDebit  = allExpenses.reduce((s,e)=>s+(e.type!=='credit'?parseFloat(e.amount)||0:0),0);
  const overallBalance = overallCredit - overallDebit;

  // Derive selectedMonth from dateTo for DailyTracker
  const trackerMonth = dateTo.slice(0,7);

  const COL_SPAN = 9 + (canEditDelete ? 2 : 0); // 9 base (expand+8 data) + checkbox + actions when authorized

  return (
    <div className="manage-expenses">
      {/* ── Toast ── */}
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* ── Deleting overlay ── */}
      {deleting && (
        <div style={{
          position:'fixed', inset:0, zIndex:99998,
          background:'rgba(0,0,0,0.25)', display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <div style={{
            background:'#fff', borderRadius:12, padding:'24px 36px',
            boxShadow:'0 8px 32px rgba(0,0,0,.2)', textAlign:'center',
          }}>
            <div style={{fontSize:28, marginBottom:8}}>⏳</div>
            <div style={{fontSize:14, fontWeight:600, color:'#374151'}}>Deleting & recalculating balances…</div>
          </div>
        </div>
      )}

      {/* ── Edit popup modal ── */}
      {editPopup && (
        <EditExpenseModal
          id={editPopup.id}
          expense={editPopup.expense}
          categories={categories}
          senders={senders}
          receivers={receivers}
          descriptions={descriptions}
          allExpenses={allExpenses}
          showToast={showToast}
          onSave={() => { setEditPopup(null); fetchRangeExpenses(); fetchAllExpenses(); }}
          onClose={() => setEditPopup(null)}
        />
      )}

      {/* ── Header ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginBottom:14}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <h2 style={{margin:0}}>Manage Expenses</h2>
          {ottCount>0&&<span style={{fontSize:11,background:'#fff7ed',color:'#c2410c',border:'1px solid #fed7aa',borderRadius:99,padding:'2px 10px',fontWeight:700}}>⚠️ {ottCount} OTT</span>}
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <label style={{fontSize:11,fontWeight:600,color:'#6b7280'}}>From</label>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
            style={{padding:'5px 10px',borderRadius:6,border:'1px solid #d1d5db',fontSize:13}} />
          <label style={{fontSize:11,fontWeight:600,color:'#6b7280'}}>To</label>
          <input type="date" value={dateTo} max={toISO(today)} onChange={e=>setDateTo(e.target.value)}
            style={{padding:'5px 10px',borderRadius:6,border:'1px solid #d1d5db',fontSize:13}} />
          <button onClick={()=>setShowTracker(v=>!v)}>{showTracker?'Hide':'📅'} Tracker</button>
          {isAnalyticsRole&&<button onClick={()=>setShowAnalytics(v=>!v)}>{showAnalytics?'Hide':'📊'} Analytics</button>}
          <button onClick={()=>setShowCashMatch(v=>!v)} style={{background:showCashMatch?'#fef3c7':'',border:showCashMatch?'1.5px solid #f59e0b':''}}>
            {showCashMatch?'Hide':'💰'} Cash Match
          </button>
          <button onClick={()=>setShowForm(v=>!v)}>{showForm?'✕ Cancel':'+ Add'}</button>
        </div>
      </div>

      {/* ── Edit alert banners ── */}
      {isNotifyRole && editAlerts.length > 0 && (
        <div style={{ marginBottom:12 }}>
          {editAlerts.map((a, i) => (
            <div key={i} style={{
              display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10,
              background:'#fffbeb', border:'1.5px solid #f59e0b', borderRadius:8,
              padding:'8px 12px', marginBottom:6, fontSize:12,
            }}>
              <div>
                <span style={{ fontWeight:800, color:'#b45309' }}>⚠ EXPENSE EDITED — </span>
                <span style={{ color:'#78350f' }}>
                  <strong>{a.editedBy}</strong> modified a{' '}
                  {a.expenseDate ? new Date(a.expenseDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}) : ''}{' '}
                  entry. Was: {a.before?.type==='credit'?'↑':'↓'}₹{a.before?.amount}
                  {a.before?.description ? ` "${a.before.description}"` : ''} →
                  Now: {a.after?.type==='credit'?'↑':'↓'}₹{a.after?.amount}
                  {a.after?.description ? ` "${a.after.description}"` : ''}.{' '}
                  <em style={{color:'#92400e'}}>Review for accuracy.</em>
                </span>
              </div>
              <button onClick={() => setEditAlerts(p => p.filter((_,j) => j!==i))}
                style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:14, flexShrink:0 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* ── Analytics ── */}
      {isAnalyticsRole&&showAnalytics&&<ExpenseAnalytics monthExpenses={expenses} allExpenses={allExpenses}/>}

      {/* ── Cash Match Panel ── */}
      {showCashMatch && (() => {
        // Compute per-person cash balance from filtered expenses:
        // Credits given TO a receiver = money out to them (they hold cash)
        // Debits FROM a sender = money returned / spent by them
        const personMap = {};
        expenses.forEach(e => {
          if (e.paymentMode !== 'Cash') return;
          const receiver = (e.receiver || '').trim().toLowerCase();
          const sender = (e.sender || '').trim().toLowerCase();
          if (e.type === 'credit' && receiver) {
            if (!personMap[receiver]) personMap[receiver] = { given: 0, spent: 0, name: e.receiver.trim() };
            personMap[receiver].given += e.amount || 0;
          }
          if (e.type === 'debit' && receiver) {
            if (!personMap[receiver]) personMap[receiver] = { given: 0, spent: 0, name: e.receiver.trim() };
            personMap[receiver].spent += e.amount || 0;
          }
        });
        const people = Object.values(personMap).filter(p => p.given > 0).map(p => ({
          ...p, balance: p.given - p.spent
        })).sort((a, b) => b.balance - a.balance);
        return (
          <div style={{ background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 12, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#92400e' }}>Cash in Hand — Reconciliation</h3>
              <span style={{ fontSize: 11, color: '#a16207' }}>Based on filtered date range</span>
            </div>
            {people.length === 0 ? (
              <div style={{ fontSize: 13, color: '#a16207' }}>No cash transactions found in this period.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f59e0b' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: '#92400e', fontWeight: 700 }}>Person</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', color: '#92400e', fontWeight: 700 }}>Given</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', color: '#92400e', fontWeight: 700 }}>Spent</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', color: '#92400e', fontWeight: 700 }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map(p => (
                    <tr key={p.name} style={{ borderBottom: '1px solid #fde68a' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{p.name}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#16a34a' }}>{fmt(p.given)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#dc2626' }}>{fmt(p.spent)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: p.balance > 0 ? '#d97706' : p.balance < 0 ? '#dc2626' : '#16a34a' }}>
                        {fmt(Math.abs(p.balance))}{p.balance > 0 ? ' unaccounted' : p.balance < 0 ? ' overspent' : ' settled'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}

      {/* ── Daily tracker ── */}
      {showTracker&&<DailyTracker expenses={expenses} selectedMonth={trackerMonth} onMarkOTT={handleMarkOTT} showAudit={canEditDelete}/>}

      {/* ── Add Expense Modal ── */}
      {showForm&&(
        <div style={{position:'fixed',inset:0,zIndex:10000,background:'rgba(15,23,42,0.6)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e=>{if(e.target===e.currentTarget){setShowForm(false);setNewExpense(defaultExpense);setAmountInWords('');}}}>
          <div style={{background:'#fff',borderRadius:20,width:'94%',maxWidth:580,maxHeight:'92vh',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,.25)',display:'flex',flexDirection:'column'}}>
            {/* Header */}
            <div style={{padding:'20px 24px 16px',borderBottom:'1px solid #e5e7eb',display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:44,height:44,borderRadius:12,background:'linear-gradient(135deg,#dbeafe,#bfdbfe)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0}}>+</div>
              <div style={{flex:1}}>
                <div style={{fontSize:18,fontWeight:800,color:'#0f172a'}}>Add New Expense</div>
                <div style={{fontSize:12,color:'#6b7280',marginTop:2}}>Fill in the details below</div>
              </div>
              <button onClick={()=>{setShowForm(false);setNewExpense(defaultExpense);setAmountInWords('');}}
                style={{background:'#f3f4f6',border:'none',width:36,height:36,borderRadius:'50%',fontSize:18,cursor:'pointer',color:'#6b7280',display:'flex',alignItems:'center',justifyContent:'center'}}>x</button>
            </div>
            {/* Body */}
            <div style={{padding:'20px 24px 24px',overflowY:'auto',flex:1}}>
            <form onSubmit={handleSubmit}>
              {/* Amount — prominent field */}
              <div style={{marginBottom:16,background:newExpense.type==='credit'?'#f0fdf4':'#fef2f2',borderRadius:12,padding:'14px 16px',border:newExpense.type==='credit'?'1.5px solid #86efac':'1.5px solid #fca5a5'}}>
                <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:6,textTransform:'uppercase',letterSpacing:'.5px'}}>Amount (INR)</label>
                <input type="number" name="amount" value={newExpense.amount} onChange={handleChange} required
                  placeholder="0" style={{width:'100%',padding:'10px 14px',border:'none',borderRadius:8,fontSize:22,fontWeight:800,boxSizing:'border-box',background:'transparent',color:newExpense.type==='credit'?'#059669':'#dc2626',outline:'none'}}/>
                {amountInWords&&<div style={{color:newExpense.type==='credit'?'#059669':'#dc2626',fontSize:12,fontStyle:'italic',fontWeight:700,marginTop:4}}>{amountInWords}</div>}
              </div>
              {/* Type + Payment Mode + Date — 3 columns */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16}}>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Type</label>
                  <select name="type" value={newExpense.type} onChange={handleChange} required
                    style={{width:'100%',padding:'10px 12px',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:13,fontWeight:600,boxSizing:'border-box',background:'#fff',color:newExpense.type==='credit'?'#059669':'#dc2626'}}>
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Payment Mode</label>
                  <select name="Mode" value={newExpense.paymentMode} onChange={handleChange} required
                    style={{width:'100%',padding:'10px 12px',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:13,boxSizing:'border-box',background:'#fff'}}>
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Paytm">Paytm</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Date</label>
                  <input type="date" name="date" value={newExpense.date||new Date().toISOString().slice(0,10)} onChange={handleChange}
                    style={{width:'100%',padding:'10px 12px',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:13,boxSizing:'border-box',background:'#fff'}}/>
                </div>
              </div>
              {/* Sender + Receiver — side by side */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Sender</label>
                  <select name="sender" value={newExpense.sender} onChange={handleChange} required
                    style={{width:'100%',padding:'10px 12px',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:13,boxSizing:'border-box',background:'#fff',marginBottom:4}}>
                    <option value="">-- Select --</option>
                    {senders.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                  <input type="text" placeholder="Or type new…" value={newExpense.sender} onChange={handleChange} name="sender"
                    onBlur={()=>{if(newExpense.sender&&!senders.includes(newExpense.sender))setSenders(p=>[...p,newExpense.sender]);}}
                    style={{width:'100%',padding:'8px 12px',border:'1px solid #e5e7eb',borderRadius:8,fontSize:12,boxSizing:'border-box',color:'#374151'}}/>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Receiver</label>
                  <select name="receiver" value={newExpense.receiver} onChange={handleChange} required
                    style={{width:'100%',padding:'10px 12px',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:13,boxSizing:'border-box',background:'#fff',marginBottom:4}}>
                    <option value="">-- Select --</option>
                    {receivers.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                  <input type="text" placeholder="Or type new…" value={newExpense.receiver} onChange={handleChange} name="receiver"
                    onBlur={()=>{if(newExpense.receiver&&!receivers.includes(newExpense.receiver))setReceivers(p=>[...p,newExpense.receiver]);}}
                    style={{width:'100%',padding:'8px 12px',border:'1px solid #e5e7eb',borderRadius:8,fontSize:12,boxSizing:'border-box',color:'#374151'}}/>
                </div>
              </div>
              {/* Category + Description — side by side */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Category</label>
                  <select name="category" value={newExpense.category} onChange={handleChange} required
                    style={{width:'100%',padding:'10px 12px',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:13,boxSizing:'border-box',background:'#fff',marginBottom:4}}>
                    <option value="">-- Select --</option>
                    {categories.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                  <input type="text" placeholder="Or type new…" value={newExpense.category} onChange={handleChange} name="category"
                    onBlur={()=>{const v=newExpense.category.trim();if(v&&!categories.includes(v))setCategories(p=>[...p,v]);}}
                    style={{width:'100%',padding:'8px 12px',border:'1px solid #e5e7eb',borderRadius:8,fontSize:12,boxSizing:'border-box',color:'#374151'}}/>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Description</label>
                  <DescriptionAutocomplete name="description" value={newExpense.description} onChange={handleChange}
                    suggestions={descriptions} required style={{}}/>
                </div>
              </div>
              {/* Transaction ID */}
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Transaction ID</label>
                <input type="text" name="transactionId" value={newExpense.transactionId} onChange={handleChange} required
                  disabled={newExpense.paymentMode==='Cash'} placeholder={newExpense.paymentMode==='Cash'?'Cash':'Enter Transaction ID'}
                  style={{width:'100%',padding:'10px 12px',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:13,boxSizing:'border-box',
                    background:newExpense.paymentMode==='Cash'?'#f3f4f6':'#fff'}}/>
              </div>
              {/* Notes */}
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:11,fontWeight:700,color:'#374151',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Notes</label>
                <textarea name="notes" value={newExpense.notes||''} onChange={handleChange} placeholder="Any additional notes..."
                  style={{width:'100%',padding:'10px 12px',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:13,boxSizing:'border-box',minHeight:50,resize:'vertical',fontFamily:'inherit'}}/>
              </div>
              {/* Buttons */}
              <div style={{display:'flex',gap:12,justifyContent:'flex-end',paddingTop:8,borderTop:'1px solid #f3f4f6'}}>
                <button type="button" onClick={()=>{setShowForm(false);setNewExpense(defaultExpense);setAmountInWords('');}}
                  style={{padding:'12px 28px',borderRadius:10,border:'1.5px solid #d1d5db',background:'#fff',cursor:'pointer',fontSize:14,fontWeight:600,color:'#374151'}}>
                  Cancel
                </button>
                <button type="submit"
                  style={{padding:'12px 36px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#2563eb,#1d4ed8)',color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14,boxShadow:'0 4px 14px rgba(37,99,235,.35)',letterSpacing:'.3px'}}>
                  Add Expense
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Filters + Search + Bulk delete bar ── */}
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
        <button onClick={()=>setFilterType(filterType==='credit'?'':'credit')}
          style={{padding:'5px 12px',borderRadius:99,border:filterType==='credit'?'1.5px solid #059669':'1.5px solid #d1d5db',
            background:filterType==='credit'?'#ecfdf5':'#fff',color:filterType==='credit'?'#059669':'#6b7280',
            fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
          ↑ Credits
        </button>
        <button onClick={()=>setFilterType(filterType==='debit'?'':'debit')}
          style={{padding:'5px 12px',borderRadius:99,border:filterType==='debit'?'1.5px solid #dc2626':'1.5px solid #d1d5db',
            background:filterType==='debit'?'#fef2f2':'#fff',color:filterType==='debit'?'#dc2626':'#6b7280',
            fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
          ↓ Debits
        </button>
        <select value={filterCategory} onChange={e=>setFilterCategory(e.target.value)}
          style={{padding:'6px 8px',borderRadius:6,border:'1px solid #d1d5db',fontSize:12,background:'#fff',minWidth:100,maxWidth:150}}>
          <option value="">All Categories</option>
          {uniqueCategories.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <input type="text" placeholder="Filter description…" value={filterDesc} onChange={e=>setFilterDesc(e.target.value)}
          style={{padding:'6px 10px',borderRadius:6,border:'1px solid #d1d5db',fontSize:12,flex:'0 1 160px',minWidth:100}}/>
        <input type="text" placeholder="Search all…" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
          style={{padding:'6px 10px',borderRadius:6,border:'1px solid #d1d5db',fontSize:13,flex:'1 1 160px'}}/>
        {(filterCategory || filterDesc || filterType) && (
          <button onClick={()=>{setFilterCategory('');setFilterDesc('');setFilterType('');}}
            style={{padding:'5px 10px',borderRadius:6,border:'1px solid #d1d5db',background:'#fef2f2',color:'#dc2626',fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
            Clear Filters
          </button>
        )}
        <span style={{fontSize:12,color:'#94a3b8',fontWeight:600,whiteSpace:'nowrap'}}>{filtered.length}/{expenses.length} records</span>
        {canEditDelete && selectedExpIds.size > 0 && (
          <button onClick={handleBulkDelete}
            style={{padding:'6px 16px',borderRadius:8,border:'none',background:'#dc2626',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>
            Delete {selectedExpIds.size} selected
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div style={{overflowX:'auto'}}>
        <table className="expense-table" style={{fontSize:12}}>
          <thead>
            <tr>
              {canEditDelete && (
                <th style={{width:28,textAlign:'center'}}>
                  <input type="checkbox" checked={sorted.length>0 && selectedExpIds.size===sorted.length}
                    onChange={toggleSelectAll} style={{cursor:'pointer',width:14,height:14}} title="Select all"/>
                </th>
              )}
              <th style={{width:28}}></th>
              {[['date','Date'],['credit','↑ Credit'],['debit','↓ Debit'],['balance','Balance'],['sender','Sender'],['receiver','Receiver'],['category','Category'],['description','Description'],
                ...(canEditDelete?[['actions','Actions']]:[])]
              .map(([col,label])=>(
                <th key={col}
                  onClick={()=>{ if(!['actions','balance','credit','debit'].includes(col)){ setSortAsc(s=>sortKey===col?!s:true); setSortKey(col); }}}
                  style={{cursor:!['actions','balance','credit','debit'].includes(col)?'pointer':'default',
                    background:col==='credit'?'#f0fdf4':col==='debit'?'#fef2f2':col==='balance'?'#eff6ff':undefined,
                    whiteSpace:'nowrap'}}>
                  {label}{sortKey===col?(sortAsc?' ↑':' ↓'):''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(e=>{
              const isOTT = e.category==='OTT';
              const expanded = expandedRows.has(e._id);
              const isSelected = selectedExpIds.has(e._id);
              return (
                <React.Fragment key={e._id}>
                  <tr style={{background:isSelected?'#eef2ff':isOTT?'#fff7ed':undefined, opacity:isOTT&&!isSelected?0.85:1}}
                    onClick={()=>toggleRow(e._id)} className="expandable-row">
                    {canEditDelete && (
                      <td onClick={ev=>ev.stopPropagation()} style={{textAlign:'center'}}>
                        <input type="checkbox" checked={isSelected} onChange={()=>toggleExpSelect(e._id)}
                          style={{cursor:'pointer',width:14,height:14}}/>
                      </td>
                    )}
                    <td style={{textAlign:'center',cursor:'pointer',color:'#6b7280'}}>
                      {expanded?<FaChevronDown size={10}/>:<FaChevronRight size={10}/>}
                    </td>
                    <td style={{whiteSpace:'nowrap',fontSize:11}}>{fmtDay(e.date)}</td>
                    <td style={{background:'#f0fdf4',color:'#16a34a',fontWeight:700,textAlign:'right',whiteSpace:'nowrap'}}>
                      {e.type==='credit'?fmt(e.amount):''}
                    </td>
                    <td style={{background:'#fef2f2',color:isOTT?'#9ca3af':'#dc2626',fontWeight:700,textAlign:'right',whiteSpace:'nowrap'}}>
                      {e.type!=='credit'?(isOTT?'OTT':fmt(e.amount)):''}
                    </td>
                    <td style={{background:'#eff6ff',fontWeight:700,textAlign:'right',whiteSpace:'nowrap'}}>{fmt(e.balanceAfterTxn)}</td>
                    <td>{e.sender}</td>
                    <td>{e.receiver}</td>
                    <td>
                      {isOTT
                        ? <span style={{background:'#fff7ed',color:'#c2410c',border:'1px solid #fed7aa',borderRadius:99,padding:'1px 8px',fontSize:11,fontWeight:700}}>OTT</span>
                        : e.category}
                    </td>
                    <td style={{maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.description}</td>
                    {canEditDelete&&(
                      <td onClick={ev=>ev.stopPropagation()}>
                        <div style={{display:'flex',gap:10}}>
                          <FaEdit onClick={()=>handleEdit(e._id,e)} style={{cursor:'pointer',color:'green',height:20}} title="Edit"/>
                          <FaTrash onClick={()=>handleDelete(e._id,e)} style={{cursor:'pointer',color:'red',height:20}} title="Delete"/>
                        </div>
                      </td>
                    )}
                  </tr>
                  {expanded&&<ExpandedRow e={e} colSpan={COL_SPAN} showAudit={canEditDelete}/>}
                </React.Fragment>
              );
            })}
            {sorted.length===0&&<tr><td colSpan={COL_SPAN} style={{textAlign:'center',padding:24,color:'#9ca3af'}}>No expenses in this date range.</td></tr>}
          </tbody>
          <tfoot>
            <tr style={{fontWeight:700,background:'#f9fafb',borderTop:'2px solid #e5e7eb'}}>
              <td colSpan={canEditDelete?3:2} style={{textAlign:'right',color:'#374151'}}>Range Total →</td>
              <td style={{background:'#f0fdf4',color:'#16a34a',textAlign:'right'}}>{fmt(totalCredit)}</td>
              <td style={{background:'#fef2f2',color:'#dc2626',textAlign:'right'}}>{fmt(totalDebit)}</td>
              <td colSpan={COL_SPAN-(canEditDelete?5:4)}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
